import dayjs from "dayjs";
import { URLSearchParams } from "url";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button, Tooltip } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";

import { useAlert } from "../../contexts/AlertContext";
import { parseSearchParams } from "../../pages/Jobs";
import * as Objects from "../../services/api";
import { currentUser } from "../../services/api/AuthUser";

const makeCsv = (items: Array<any>, fields: Array<string>): string => {
  let data = fields.join(",") + "\n";
  for (const item of items) {
    const row = fields.map((field) => {
      const value = String(item[field]);
      // Double all existing double quotes and wrap value in double quotes.
      // Escapes double quotes and commas in values.
      return `"${value.replace(/"/g, '""')}"`;
    });
    data += row.join(",") + "\n";
  }
  return data;
};

const validateExport = (searchParams: URLSearchParams): string => {
  const parsedParams = Object.fromEntries(searchParams);

  if (searchParams.size <= 2) {
    return "Please perform a search before exporting.";
  }

  if (searchParams.has("status")) {
    return "Export by status is not supported.";
  }

  if (
    searchParams.has("start") &&
    searchParams.has("starttime") &&
    searchParams.has("end") &&
    searchParams.has("endtime")
  ) {
    const start = parsedParams["start"];
    const starttime = parsedParams["starttime"];
    const end = parsedParams["end"];
    const endtime = parsedParams["endtime"];
    const startDate = dayjs(`${start} ${starttime}`);
    const endDate = dayjs(`${end} ${endtime}`);

    if (endDate.diff(startDate, "day", true) > 7) {
      return "Exceeded export limit, date range must be within 7 days.";
    }
  }

  return "";
};

const JobsExportButton: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [disableExport, setDisableExport] = useState(true);
  const [disableReason, setDisableReason] = useState("");
  const { alert } = useAlert();
  const gridApiRef = useGridApiContext();

  const handleExport = async () => {
    const limit = 10000;
    const defaultParams = { page: 1, limit: limit };
    const queryParams = { ...parseSearchParams(searchParams), ...defaultParams };
    const fieldsToExport = gridApiRef.current
      .getVisibleColumns()
      .filter((c) => !c.disableExport)
      .map((c) => c.field);

    setDisableExport(true);
    try {
      const jobs = await Objects.Jobs.list(queryParams);
      if (jobs.count === 0) {
        alert.warning("No records found for export.");
        return;
      }

      if (jobs.count > limit) {
        alert.error(
          `Export failed: the total result is ${jobs.count}, exceeding the maximum limit of ${limit}.`,
        );
        return;
      }

      const csvData = makeCsv(jobs.items, fieldsToExport);
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", href);
      link.setAttribute("download", `atp_export_${currentUser.id}_${Math.round(Date.now() / 1000)}.csv`);
      link.click();
      URL.revokeObjectURL(href);
    } finally {
      setDisableExport(false);
    }
  };

  useEffect(() => {
    const reason = validateExport(searchParams);
    setDisableReason(reason);
    setDisableExport(reason !== "");
  }, [searchParams]);

  return (
    <Tooltip title={disableReason}>
      <span>
        <Button
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={disableExport}
          data-testid="grid-export-button"
        >
          Export
        </Button>
      </span>
    </Tooltip>
  );
};

export default JobsExportButton;
